/**
 * EventStickyBar — phone only. The single visible action while reading.
 *
 * It is the last child of `.evb-zone` and sticks to the BOTTOM of the viewport,
 * so it is always within reach while reading. Sticking to the bottom is what
 * makes that safe — a bottom-stuck element can never rise above its natural
 * position, so it cannot reach the site header.
 *
 * REGISTER IS THE ACTION, not a jump. It pointed at the entry band while the
 * band held a form; with no band there is nowhere to travel to, so pressing it
 * opens the entry modal directly.
 *
 * IT ALSO CARRIES THE CONFIRMATION, and that is not a nicety. The rail says
 * "you are registered" on a desktop — but the rail is `display:none` on a
 * phone, so a bar that stood down once somebody had entered left them with no
 * trace of their own registration anywhere on the page, and no way to their
 * passes. The bar is the rail's stand-in on a phone; it has to stand in for
 * both of the rail's states, not just the one with a button in it.
 *
 * The two slots are reused rather than restyled: the left one carries a value
 * over its label — "Free / Entry fee" becomes "Registered / Your entry" — and
 * the right one carries whichever action is live.
 *
 * It never leaves while scrolling — in either direction. The register action
 * is the one thing a phone visitor must always be able to reach, so it no
 * longer carries `data-arcee-dock="scroll"` and nothing hides it.
 */

import React from 'react';
import { PENDING_TITLE, PENDING_MORE_BAR } from './pendingPayment';

export default function EventStickyBar({
  isPaid, feeAmount, buttons, isCompleted, onAction, pendingPayment, onBookMore,
}) {
  const available = Array.isArray(buttons) ? buttons : [];
  const register = available.find(b => String(b.id) === 'register');
  const myRegistrations = available.find(b => String(b.id) === 'view_my_registrations');
  const entered = Boolean(register && isCompleted && isCompleted(register));
  // Below `entered` for the same reason as the rail: a confirmed entry is the
  // better answer whenever both happen to be true.
  const paying = Boolean(pendingPayment) && !entered;

  // Entry is not on offer at all — closed, or invitation only. Nothing to
  // press and nothing to report.
  if (!register) return null;

  // Two actions need two rows on a phone -- see .evb-bar--two for the widths
  // that decide it. Only this state has two, so only this state gets the class.
  // Not while a payment is outstanding -- see the rail for why this reads the
  // prop rather than `paying`, which is false whenever `entered` is true.
  const canBookMore = entered && Boolean(onBookMore) && !pendingPayment;
  const twoActions = canBookMore;
  // The dock's half of the rail's notice. Same state, same reason: the dock
  // drops "Book more" while a payment is outstanding, and dropping it without
  // a word is what made it look broken. Needs `onBookMore` too, so a card that
  // never offers the link does not explain its absence.
  const payingForMore = entered && Boolean(pendingPayment) && Boolean(onBookMore);
  // When self check-in opens. The rail's sentence carries it on a desktop; the
  // rail is hidden on a phone, so the dock says it in the same full-width slot.
  const checkInHint = entered && !payingForMore ? register.action?.on_completed?.check_in_hint : '';

  return (
    <div className={`evb-bar${twoActions ? ' evb-bar--two' : ''}`
      + `${payingForMore || checkInHint ? ' evb-bar--note' : ''}`}>
      {entered ? (
        <>
          {/* ABOVE the entry line, not beside it: at 320px the dock has no
              room for a third thing on one row, and this is the only part of
              the dock that is news. The modifier gives it the full width and
              lets the entry line and the pass button keep their own row. */}
          {payingForMore && (
            <div className="evb-bar__note" role="status">
              <b>{PENDING_MORE_BAR}</b>
            </div>
          )}
          {checkInHint && (
            <div className="evb-bar__note evb-bar__note--checkin" role="status">
              <span>{checkInHint}</span>
            </div>
          )}
          <div
            className="evb-bar__fee"
            data-arcee-action-state="completed"
            data-arcee-action-id={String(register.id)}
          >
            <b>Registered</b>
            <span>Your entry</span>
          </div>
          {myRegistrations && (
            <a className="evb-go" href={myRegistrations.href}>
              {myRegistrations.label}
            </a>
          )}
          {/* The dock's version of the rail's link. Shorter label because the
              bar is a single line on a phone and the pass link beside it is
              the longer of the two. */}
          {canBookMore && (
            <button
              type="button"
              className="evb-link evb-link--btn"
              onClick={() => onBookMore(register)}
            >
              Book more
            </button>
          )}
        </>
      ) : paying ? (
        // The dock is a single line on a phone, so it takes the same two-part
        // shape as the other states — a status and its label — and leaves the
        // full sentence to the rail. What matters here is that the button that
        // would charge them twice is gone.
        <div className="evb-bar__fee" role="status">
          <b>{PENDING_TITLE}</b>
          <span>Awaiting confirmation</span>
        </div>
      ) : (
        <>
          <div className="evb-bar__fee">
            {isPaid && <b className="evb-fee--paid">{feeAmount}</b>}
            <b className="evb-fee--free">Free</b>
            <span>Entry fee</span>
          </div>
          <button type="button" className="evb-go" onClick={() => onAction(register)}>
            Register
          </button>
        </>
      )}
    </div>
  );
}
